import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { notification } from 'antd';
import { Clock, Download, Plus } from 'lucide-react';

import {
    addCouponService,
    couponDeleteService,
    couponStatusChange,
    editCouponService,
    getAllCouponsService,
} from '../../../services/otherService';
import { getAllPrgramWithId } from '../../../services/programService';
import { getCampDetailService } from '../../../services/userServices';
import { ProgramLoader } from '../../reuse/SVG';

import BulkActionBar from './BulkActionBar';
import CouponCardList from './CouponCardList';
import CouponEmptyState from './CouponEmptyState';
import CouponFilterBar from './CouponFilterBar';
import CouponFormModal from './CouponFormModal';
import CouponPagination from './CouponPagination';
import CouponTable from './CouponTable';
import ConfirmDialog from './ConfirmDialog';
import { fromApi, toCreateParams, toEditParams } from './couponAdapter';
import { csvFileName, downloadCsv } from './couponCsv';
import {
    EXPIRING_WINDOW_DAYS,
    STATUS_ORDER,
    filterCoupons,
    getCouponStatus,
    isExpiringSoon,
    sortCoupons,
} from './couponStatus';

import './coupons.generated.css';

/**
 * The Coupon tab of /payments.
 *
 * Unlike the page it replaces, the whole coupon list is fetched once and searched, filtered,
 * sorted and paged in memory. A camp has tens of coupons, not thousands, and holding them locally
 * is what makes the status counts on the filter chips true across the list rather than true only
 * of the page you happen to be on. It also drops a server round-trip per keystroke of search.
 *
 * `PAGE_LIMIT` is the ceiling on that. It is far above any real camp's count; the guard below
 * exists so that if one ever exceeds it we say so instead of quietly showing a subset.
 */
const PAGE_LIMIT = 500;
const PAGE_SIZE = 15;

/**
 * How many coupons a bulk action has in flight at once.
 *
 * There is no bulk endpoint — `coupon/statusChange` and `coupon/delete` each take a single id — so
 * a bulk action is N requests. Firing all of them at once would open 200 connections and put 200
 * `adminService.isAdmin` user lookups on the server at the same instant; doing them strictly one
 * after another would take about six seconds for thirty. Four keeps both ends comfortable.
 */
const BULK_CONCURRENCY = 4;

const DEFAULT_FILTERS = { query: '', status: 'All', type: 'All', expiringOnly: false };
const DEFAULT_SORT = { key: 'status', direction: 'asc' };

/**
 * A request can fail two ways: the interceptor rejects on an HTTP error, or it resolves with
 * `statusCode: 0` and the reason in `error.responseMessage`. Both have to be handled, and neither
 * may be treated as success — this page keeps the list in local state, so a silent failure would
 * leave the screen showing a change the server never made.
 */
const failed = res => !res?.data?.statusCode;

// `coupon/create` answers with `createMapper`'s `{ message, result: <saved coupon> }` as its
// responseData. Empty string when the id is missing, so callers can treat it as "not found".
const createdCouponId = res => String(res?.data?.responseData?.result?._id || '');

const failureMessage = resOrError =>
    resOrError?.data?.error?.responseMessage ||
    resOrError?.response?.data?.error?.responseMessage ||
    'Something went wrong. Please try again.';

const notifyFailure = (title, resOrError) =>
    notification.open({ message: title, description: failureMessage(resOrError) });

const Coupons = props => {
    const { campCode, categories } = props;

    const [coupons, setCoupons] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [page, setPage] = useState(1);

    const [programList, setProgramList] = useState([]);
    const [campId, setCampId] = useState(null);

    const [modal, setModal] = useState({ open: false, mode: 'create', coupon: null });
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);

    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(null);

    /* ---------- data ---------- */

    /**
     * Resolves to the loaded rows so callers that need to find one — creating, say — can.
     *
     * `quiet` skips the loading state. A bulk action refetches to resync with the server, and
     * swapping the table for the spinner at that point would flash the list away right after the
     * rows the director was looking at changed.
     */
    const loadCoupons = useCallback(({ quiet = false } = {}) => {
        if (!campCode) return Promise.resolve([]);

        if (!quiet) setLoading(true);
        return getAllCouponsService(`?limit=${PAGE_LIMIT}&page=0&campCode=${campCode}&search=`)
            .then(res => {
                if (failed(res)) {
                    notifyFailure('Could not load coupons', res);
                    return [];
                }

                const result = res.data.responseData.result;
                const rows = (result.data || []).map(fromApi);
                setCoupons(rows);
                setTotalCount(result.totalCount || 0);
                return rows;
            })
            .catch(error => {
                notifyFailure('Could not load coupons', error);
                return [];
            })
            .finally(() => {
                if (!quiet) setLoading(false);
            });
    }, [campCode]);

    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    // Options for the create form. Fetched once alongside the list rather than when the modal
    // opens, so the form is ready the moment it appears.
    useEffect(() => {
        if (!campCode) return;

        // Failures here leave the create form with empty program pickers rather than breaking the
        // list, so they are swallowed rather than surfaced — the submit is what needs to be loud.
        getAllPrgramWithId(`?campCode=${campCode}`)
            .then(res => {
                if (res?.data?.statusCode) setProgramList(res.data.responseData.result || []);
            })
            .catch(() => setProgramList([]));

        getCampDetailService(`?campCode=${campCode}`)
            .then(res => {
                if (res?.data?.statusCode) setCampId(res.data.responseData.result?._id);
            })
            .catch(() => setCampId(null));
    }, [campCode]);

    /* ---------- derived list ---------- */

    const statusCounts = useMemo(() => {
        const counts = {};
        STATUS_ORDER.forEach(status => {
            counts[status] = 0;
        });
        coupons.forEach(coupon => {
            const status = getCouponStatus(coupon);
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    }, [coupons]);

    const expiringSoon = useMemo(() => coupons.filter(coupon => isExpiringSoon(coupon)), [coupons]);

    const filtered = useMemo(() => filterCoupons(coupons, filters), [coupons, filters]);
    const sorted = useMemo(() => sortCoupons(filtered, sort), [filtered, sort]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const rangeStart = sorted.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, sorted.length);

    /**
     * The selection a bulk action actually operates on.
     *
     * Derived by intersecting the raw ids with the *filtered* list rather than trusting
     * `selectedIds` directly. Selecting rows and then narrowing the filter would otherwise leave
     * coupons selected that are no longer on screen, and a bulk delete would silently take rows
     * the director could not see. Intersecting means the bar's count visibly drops instead.
     */
    const selected = useMemo(
        () => filtered.filter(coupon => selectedIds.includes(coupon.id)),
        [filtered, selectedIds]
    );

    const pageSelected = visible.length > 0 && visible.every(item => selectedIds.includes(item.id));
    const allMatchingSelected =
        sorted.length > 0 && sorted.every(item => selectedIds.includes(item.id));

    const activeCount = statusCounts.Active || 0;
    const redeemedCount = coupons.reduce((total, coupon) => total + coupon.redeemed, 0);
    const filtersApplied =
        Boolean(filters.query) ||
        filters.status !== 'All' ||
        filters.type !== 'All' ||
        filters.expiringOnly;

    /* ---------- list controls ---------- */

    const patchFilters = patch => {
        setFilters(previous => ({ ...previous, ...patch }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setPage(1);
    };

    /* ---------- selection ---------- */

    const handleSelect = (id, isSelected) =>
        setSelectedIds(previous =>
            isSelected ? [...previous, id] : previous.filter(item => item !== id)
        );

    /** The header checkbox: this page's rows only, which is what the box's own state reflects. */
    const handleSelectPage = isSelected =>
        setSelectedIds(previous => {
            const ids = visible.map(item => item.id);
            return isSelected
                ? Array.from(new Set([...previous, ...ids]))
                : previous.filter(item => !ids.includes(item));
        });

    /**
     * The escape hatch offered once a whole page is ticked. Without it, "filter to Expired and
     * deactivate them all" would mean visiting every page of the filtered set fifteen rows at a
     * time — which is most of the reason to have bulk actions alongside the filter chips.
     */
    const handleSelectAllMatching = () =>
        setSelectedIds(previous =>
            Array.from(new Set([...previous, ...sorted.map(item => item.id)]))
        );

    const clearSelection = () => setSelectedIds([]);

    const handleSort = key => {
        setPage(1);
        setSort(previous =>
            previous.key === key
                ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: key === 'name' || key === 'status' ? 'asc' : 'desc' }
        );
    };

    /* ---------- actions ---------- */

    const flash = id => {
        setHighlightId(id);
        window.setTimeout(() => setHighlightId(current => (current === id ? null : current)), 4000);
    };

    const handleToggle = coupon => {
        setTogglingId(coupon.id);

        // `status` is sent as a string: the server validates it with lodash `isEmpty`, which is
        // true for the number 0 and would reject a deactivation.
        couponStatusChange({ couponId: coupon.id, status: coupon.active ? '0' : '1' })
            .then(res => {
                if (failed(res)) {
                    notifyFailure('Could not change status', res);
                    return;
                }
                // Patch the row in place rather than refetching: the whole list is in state, and a
                // refetch would reset the filters' effect on which page you are looking at.
                setCoupons(previous =>
                    previous.map(item =>
                        item.id === coupon.id ? { ...item, active: !item.active } : item
                    )
                );
                notification.open({
                    message: 'Coupon status',
                    description: `“${coupon.name}” is now ${coupon.active ? 'inactive' : 'active'}.`,
                });
            })
            .catch(error => notifyFailure('Could not change status', error))
            .finally(() => setTogglingId(null));
    };

    /* ---------- bulk ---------- */

    /**
     * Applies `call` to every row, a few at a time, and reports honestly on what happened.
     *
     * The awkward part of bulk-over-single-endpoints is partial failure: nine of twelve succeed and
     * a single "done" toast would be a lie. So every result is accounted for individually, the list
     * is refetched from the server afterwards regardless of outcome — local state cannot be trusted
     * to know which of the twelve actually changed — and anything that failed stays selected so a
     * retry is one click rather than a hunt.
     */
    const runBulk = async (rows, call, { verb, past, subject }) => {
        if (!rows.length) return;

        setBulkBusy({ verb, done: 0, total: rows.length });
        const failures = [];

        for (let index = 0; index < rows.length; index += BULK_CONCURRENCY) {
            const batch = rows.slice(index, index + BULK_CONCURRENCY);
            // Sequential by batch on purpose — the cap is the point. eslint's no-await-in-loop
            // wants Promise.all over the whole set, which is exactly what we are avoiding.
            // eslint-disable-next-line no-await-in-loop
            const settled = await Promise.allSettled(batch.map(row => call(row)));

            settled.forEach((result, position) => {
                const row = batch[position];
                // Both failure modes this API has: a rejected promise, or a 200 carrying
                // statusCode 0 with the reason in the body.
                if (result.status === 'rejected') {
                    failures.push({ row, message: failureMessage(result.reason) });
                } else if (failed(result.value)) {
                    failures.push({ row, message: failureMessage(result.value) });
                }
            });

            setBulkBusy({ verb, done: Math.min(index + BULK_CONCURRENCY, rows.length), total: rows.length });
        }

        setBulkBusy(null);
        await loadCoupons({ quiet: true });

        const succeeded = rows.length - failures.length;

        if (!failures.length) {
            setSelectedIds([]);
            // `subject` lets a single-row action name the coupon — “Deleted “Summer 25”.” reads
            // better than “Deleted 1 coupon.” when the director clicked one specific trash icon.
            notification.open({
                description: `${past} ${
                    subject || `${succeeded} ${succeeded === 1 ? 'coupon' : 'coupons'}`
                }.`,
            });
            return;
        }

        setSelectedIds(failures.map(failure => failure.row.id));

        const named = failures.slice(0, 3).map(failure => failure.row.code).join(', ');
        const more = failures.length > 3 ? ` and ${failures.length - 3} more` : '';
        notification.open({
            message: `${past} ${succeeded} of ${rows.length}`,
            description: `${failures.length} failed: ${named}${more}. ${failures[0].message}`,
        });
    };

    const setSelectedActive = active => {
        // Skip rows already in the target state rather than sending a no-op request each — and so
        // the count in the toast is what actually changed, not what happened to be ticked.
        const rows = selected.filter(coupon => coupon.active !== active);

        if (!rows.length) {
            notification.open({
                description: `Already ${active ? 'active' : 'inactive'}. Nothing to change.`,
            });
            return;
        }

        runBulk(rows, row => couponStatusChange({ couponId: row.id, status: active ? '1' : '0' }), {
            verb: active ? 'Activating' : 'Deactivating',
            past: active ? 'Activated' : 'Deactivated',
        });
    };

    /* ---------- delete ---------- */

    /** One shape serves the row's trash icon and the bulk bar. */
    const requestDelete = coupon =>
        setDeleteTarget({
            ids: [coupon.id],
            label: `“${coupon.name}”`,
            redeemed: coupon.redeemed,
            anyActive: coupon.active,
        });

    const requestBulkDelete = () =>
        setDeleteTarget({
            ids: selected.map(coupon => coupon.id),
            label: `${selected.length} ${selected.length === 1 ? 'coupon' : 'coupons'}`,
            redeemed: selected.reduce((total, coupon) => total + coupon.redeemed, 0),
            anyActive: selected.some(coupon => coupon.active),
        });

    const targetRows = () => coupons.filter(item => deleteTarget.ids.includes(item.id));

    const handleDelete = async () => {
        if (!deleteTarget) return;

        const rows = targetRows();
        setDeleting(true);
        await runBulk(rows, row => couponDeleteService({ couponId: row.id }), {
            verb: 'Deleting',
            past: 'Deleted',
            subject: rows.length === 1 ? deleteTarget.label : null,
        });
        setDeleting(false);
        setDeleteTarget(null);
    };

    const handleDeactivateInstead = () => {
        if (!deleteTarget) return;

        const rows = targetRows().filter(row => row.active);
        setDeleteTarget(null);
        runBulk(rows, row => couponStatusChange({ couponId: row.id, status: '0' }), {
            verb: 'Deactivating',
            past: 'Deactivated',
        });
    };

    const handleCreate = draft => {
        setSubmitting(true);

        addCouponService(toCreateParams(draft, { campCode, campId }))
            .then(res => {
                if (failed(res)) {
                    notifyFailure('Could not create coupon', res);
                    return;
                }

                setModal({ open: false, mode: 'create', coupon: null });
                clearFilters();
                notification.open({
                    description: `Created “${draft.name.trim()}” (${draft.code.trim()}).`,
                });

                // `coupon/create` returns the saved document, but the list row is shaped by the
                // projection rather than the model, so the row is taken from the refetch. Matched
                // on id, not code: several coupons may share a code, so a code match could land on
                // an older coupon.
                const createdId = createdCouponId(res);
                loadCoupons().then(rows => {
                    const created = rows.find(item => String(item.id) === createdId);
                    if (created) flash(created.id);
                });
            })
            .catch(error => notifyFailure('Could not create coupon', error))
            .finally(() => setSubmitting(false));
    };

    /**
     * Saves the six editable fields. Mirrors `handleCreate` — same two failure modes, same refetch.
     *
     * The list is refetched rather than patched in place, unlike `handleToggle`. A toggle changes
     * one boolean this page already knows the new value of; an edit changes six fields, two of
     * which (`startDate`, `endDate`) the server rewrites through its own date conversion, and one
     * of which (`noOfTimesUsed`) feeds the derived status. Trusting the draft would let the row
     * disagree with what was actually stored.
     */
    const handleEdit = draft => {
        const target = modal.coupon;
        if (!target) return;

        setSubmitting(true);

        editCouponService(toEditParams(draft, target))
            .then(res => {
                if (failed(res)) {
                    notifyFailure('Could not save changes', res);
                    return;
                }

                setModal({ open: false, mode: 'create', coupon: null });
                notification.open({ description: `Saved changes to “${draft.name.trim()}”.` });
                loadCoupons().then(() => flash(target.id));
            })
            .catch(error => notifyFailure('Could not save changes', error))
            .finally(() => setSubmitting(false));
    };

    /**
     * Creates the clone, then optionally retires the coupon it came from.
     *
     * Two calls, because `coupon/create` has no `status` in its payload — the route destructures a
     * fixed field list and `status` is not in it — so a clone cannot be born paused. It is created
     * active and paused immediately after. The window between is a single round-trip; if the
     * director kept the source's code, a checkout in that instant could see both coupons, which
     * is how shared codes already behave.
     *
     * Each of the three steps is reported on its own. A clone that was created but not paused, or
     * created without retiring its original, has left the camp with two live codes at different
     * prices — the exact outcome this whole model exists to prevent — so saying "done" would be a
     * lie with money attached.
     */
    const handleClone = async (draft, { deactivateOriginal } = {}) => {
        const source = modal.coupon;
        if (!source) return;

        setSubmitting(true);
        const name = draft.name.trim();
        const code = draft.code.trim();

        try {
            const created = await addCouponService(toCreateParams(draft, { campCode, campId }));
            if (failed(created)) {
                notifyFailure('Could not create the clone', created);
                return;
            }

            setModal({ open: false, mode: 'create', coupon: null });

            // The id comes straight from the create response. Never look the clone up by code: it
            // may share its code with the source coupon, and pausing a code match could pause the
            // live original instead of the clone.
            const cloneId = createdCouponId(created);

            const problems = [];

            if (cloneId) {
                // `status` as a string: the server validates it with lodash `isEmpty`, which is
                // true for the number 0 and would reject every deactivation.
                const paused = await couponStatusChange({ couponId: cloneId, status: '0' }).catch(
                    error => error
                );
                if (!paused?.data?.statusCode) {
                    problems.push(`“${name}” was created but is still ACTIVE — pause it manually.`);
                }
            } else {
                problems.push(`“${name}” was created but could not be found to pause — check it.`);
            }

            if (deactivateOriginal) {
                const retired = await couponStatusChange({
                    couponId: source.id,
                    status: '0',
                }).catch(error => error);
                if (!retired?.data?.statusCode) {
                    problems.push(`${source.code} is still active — deactivate it manually.`);
                }
            }

            await loadCoupons();
            if (cloneId) flash(cloneId);

            if (problems.length) {
                notification.open({
                    message: 'Clone created, with problems',
                    description: problems.join(' '),
                });
                return;
            }

            notification.open({
                description: `Created “${name}” (${code}), paused so you can review it${
                    deactivateOriginal ? `. ${source.code} has been deactivated` : ''
                }.`,
            });
        } catch (error) {
            notifyFailure('Could not create the clone', error);
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Opens clone mode on a coupon. `deactivateOriginal` seeds the dialog's checkbox: pre-ticked
     * when the director arrived from a locked field in the edit form, because they have already
     * said this coupon needs to be different; unticked from the row action, which is just as often
     * "make me another one like this".
     */
    const openClone = (coupon, { deactivateOriginal = false } = {}) =>
        setModal({ open: true, mode: 'clone', coupon, deactivateOriginal });

    const copyToast = code => notification.open({ description: `Copied ${code} to your clipboard.` });

    /* ---------- export ---------- */

    const exportRows = (rows, scope) => {
        if (!rows.length) return;

        downloadCsv(rows, csvFileName(scope));

        // The count is the honest part. When the camp has more coupons than PAGE_LIMIT loaded, the
        // export covers only what is in memory — the banner above says so, and this says how many
        // rows actually made it into the file rather than implying the whole catalogue.
        const truncated = scope !== 'selection' && totalCount > coupons.length;
        notification.open({
            description: `Exported ${rows.length} ${rows.length === 1 ? 'coupon' : 'coupons'} to CSV.${
                truncated ? ' Only the coupons loaded on this page are included.' : ''
            }`,
        });
    };

    /* ---------- render ---------- */

    const listBody = () => {
        if (loading) {
            return (
                <div className="cpn-flex cpn-justify-center cpn-py-20">
                    <ProgramLoader />
                </div>
            );
        }

        if (sorted.length === 0) {
            return (
                <CouponEmptyState
                    filtersApplied={filtersApplied}
                    onClearFilters={clearFilters}
                    onCreate={() => setModal({ open: true, mode: 'create', coupon: null })}
                />
            );
        }

        return (
            <>
                {/* Appears only once the whole page is ticked and there is more beyond it, which is
                    exactly when "select all on this page" stops being the thing you meant. */}
                {pageSelected && !allMatchingSelected && (
                    <div className="cpn-flex cpn-flex-wrap cpn-items-center cpn-justify-center cpn-gap-2 cpn-border-b cpn-border-brand-100 cpn-bg-brand-50 cpn-px-4 cpn-py-2.5 cpn-text-sm cpn-text-brand-900">
                        <span>
                            All <strong>{visible.length}</strong> on this page are selected.
                        </span>
                        <button
                            type="button"
                            onClick={handleSelectAllMatching}
                            className="cpn-font-semibold cpn-text-brand-700 cpn-underline cpn-transition hover:cpn-text-brand-800"
                        >
                            Select all {sorted.length} matching
                        </button>
                    </div>
                )}

                <CouponTable
                    coupons={visible}
                    sort={sort}
                    highlightId={highlightId}
                    togglingId={togglingId}
                    selectedIds={selectedIds}
                    onSort={handleSort}
                    onSelect={handleSelect}
                    onSelectPage={handleSelectPage}
                    onOpen={coupon => setModal({ open: true, mode: 'edit', coupon })}
                    onClone={openClone}
                    onToggle={handleToggle}
                    onDelete={requestDelete}
                    onCopy={copyToast}
                />
                <CouponCardList
                    coupons={visible}
                    highlightId={highlightId}
                    togglingId={togglingId}
                    selectedIds={selectedIds}
                    onSelect={handleSelect}
                    onOpen={coupon => setModal({ open: true, mode: 'edit', coupon })}
                    onClone={openClone}
                    onToggle={handleToggle}
                    onDelete={requestDelete}
                    onCopy={copyToast}
                />
                <CouponPagination
                    page={currentPage}
                    totalPages={totalPages}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    totalItems={sorted.length}
                    onChange={setPage}
                />
            </>
        );
    };

    return (
        <main className="coupon-codes-page cpn-mx-auto cpn-w-full cpn-max-w-[1400px] cpn-px-4 cpn-py-8 cpn-font-sans sm:cpn-px-6">
            <div className="cpn-flex cpn-flex-col cpn-gap-5 sm:cpn-flex-row sm:cpn-items-end sm:cpn-justify-between">
                <div>
                    <h1 className="cpn-m-0 cpn-text-2xl cpn-font-extrabold cpn-tracking-tight cpn-text-neutral-900">
                        Coupon Codes
                    </h1>
                    <p className="cpn-mb-0 cpn-mt-1 cpn-text-sm cpn-text-neutral-500">
                        {coupons.length} {coupons.length === 1 ? 'code' : 'codes'} · {activeCount} active ·{' '}
                        {redeemedCount} total {redeemedCount === 1 ? 'redemption' : 'redemptions'}
                    </p>
                </div>
                <div className="cpn-flex cpn-flex-wrap cpn-gap-2 cpn-self-start">
                    <button
                        type="button"
                        onClick={() => exportRows(sorted, filtersApplied ? 'filtered' : 'all')}
                        disabled={sorted.length === 0}
                        className="cpn-inline-flex cpn-h-11 cpn-items-center cpn-justify-center cpn-gap-2 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-px-4 cpn-text-sm cpn-font-semibold cpn-text-neutral-700 cpn-transition hover:cpn-border-brand-300 hover:cpn-text-brand-700 disabled:cpn-cursor-not-allowed disabled:cpn-opacity-40 disabled:hover:cpn-border-neutral-200 disabled:hover:cpn-text-neutral-700"
                    >
                        <Download className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                        Export CSV
                    </button>
                    <button
                        type="button"
                        onClick={() => setModal({ open: true, mode: 'create', coupon: null })}
                        className="cpn-inline-flex cpn-h-11 cpn-items-center cpn-justify-center cpn-gap-2 cpn-rounded-xl cpn-bg-brand-700 cpn-px-5 cpn-text-sm cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-brand-800 focus:cpn-outline-none focus-visible:cpn-ring-4 focus-visible:cpn-ring-brand-200"
                    >
                        <Plus className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                        Create Coupon Code
                    </button>
                </div>
            </div>

            {totalCount > coupons.length && (
                <div className="cpn-mt-5 cpn-rounded-2xl cpn-border cpn-border-amber-200 cpn-bg-amber-50 cpn-px-4 cpn-py-3.5 cpn-text-sm cpn-text-amber-900">
                    Showing the first {coupons.length} of {totalCount} coupons. Search and filters only
                    cover the ones loaded.
                </div>
            )}

            {expiringSoon.length > 0 && !filters.expiringOnly && (
                <div className="cpn-mt-5 cpn-flex cpn-flex-col cpn-gap-3 cpn-rounded-2xl cpn-border cpn-border-orange-200 cpn-bg-orange-50 cpn-px-4 cpn-py-3.5 sm:cpn-flex-row sm:cpn-items-center sm:cpn-justify-between">
                    <p className="cpn-m-0 cpn-flex cpn-items-start cpn-gap-2.5 cpn-text-sm cpn-text-orange-900">
                        <Clock
                            className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-orange-600"
                            aria-hidden="true"
                        />
                        <span>
                            <strong>
                                {expiringSoon.length} {expiringSoon.length === 1 ? 'code' : 'codes'}
                            </strong>{' '}
                            {expiringSoon.length === 1 ? 'expires' : 'expire'} in the next{' '}
                            {EXPIRING_WINDOW_DAYS} days. Extend them now if families are still booking.
                        </span>
                    </p>
                    <button
                        type="button"
                        onClick={() => patchFilters({ expiringOnly: true })}
                        className="cpn-shrink-0 cpn-rounded-lg cpn-bg-orange-600 cpn-px-3.5 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-orange-700"
                    >
                        Review them
                    </button>
                </div>
            )}

            <div className="cpn-mt-6 cpn-overflow-hidden cpn-rounded-2xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-shadow-card">
                <CouponFilterBar
                    filters={filters}
                    statusCounts={statusCounts}
                    expiringCount={expiringSoon.length}
                    filtersApplied={filtersApplied}
                    onQueryChange={query => patchFilters({ query })}
                    onStatusChange={status => patchFilters({ status })}
                    onTypeChange={type => patchFilters({ type })}
                    onExpiringChange={expiringOnly => patchFilters({ expiringOnly })}
                    onClear={clearFilters}
                />
                {listBody()}
            </div>

            <CouponFormModal
                open={modal.open}
                mode={modal.mode}
                coupon={modal.coupon}
                allCoupons={coupons}
                programList={programList}
                categories={categories}
                submitting={submitting}
                deactivateOriginalByDefault={modal.deactivateOriginal}
                onClose={() => setModal({ open: false, mode: 'create', coupon: null })}
                // Reached from a locked field in the edit form, so the original is presumed to be
                // on its way out — see openClone.
                onCloneRequest={() => openClone(modal.coupon, { deactivateOriginal: true })}
                onSubmit={
                    // eslint-disable-next-line no-nested-ternary
                    modal.mode === 'edit'
                        ? handleEdit
                        : modal.mode === 'clone'
                        ? handleClone
                        : handleCreate
                }
            />

            <ConfirmDialog
                open={deleteTarget !== null}
                target={deleteTarget}
                busy={deleting}
                onConfirm={handleDelete}
                onDeactivate={handleDeactivateInstead}
                onCancel={() => setDeleteTarget(null)}
            />

            <BulkActionBar
                count={selected.length}
                busy={bulkBusy}
                onActivate={() => setSelectedActive(true)}
                onDeactivate={() => setSelectedActive(false)}
                onExport={() => exportRows(selected, 'selection')}
                onDelete={requestBulkDelete}
                onClear={clearSelection}
            />
        </main>
    );
};

const mapStateToProps = state => {
    const { userProfile } = state.user;
    const { preList } = state.program;

    return {
        campCode: userProfile.campCode,
        categories: preList.category,
    };
};

export default connect(mapStateToProps)(Coupons);
